/**
 * What a well-formed Item looks like — the rules, with nothing around them.
 *
 * Webflow's editor used to refuse a duplicate slug, a dangling reference, an
 * option that does not exist. `tools/check-content.mjs` is that refusal moved
 * into the repo. This is the half of it that judges **one Item against its
 * schema**, lifted out so that two things can share it rather than agree by
 * coincidence:
 *
 *   - `tools/check-content.mjs`, which runs over all 363 Items before a push
 *     and again in CI, and adds everything that needs the whole repo — images
 *     on disk, pinned slugs, Webflow's sitemap, collection-wide ordering;
 *   - the admin panel (`admin/`), which runs it over the one Item somebody is
 *     editing and shows each finding **on the field it belongs to**, before the
 *     Save button does anything.
 *
 * The panel could have had its own copy of these rules. It would have drifted,
 * and the drift would have been invisible: a panel that is a little more
 * permissive than the build lets someone fill in a form, save it, and find out
 * from a red deploy — which is exactly the experience the panel exists to
 * replace. One module, no copies.
 *
 * ## What it deliberately does not do
 *
 * No `fs`, no `vm`, no `require` of anything in this repo. It takes plain data
 * and returns plain data, so it runs unchanged in Node and in a Cloudflare
 * Worker, where there is no filesystem to read `content/` from. Everything that
 * needs to *find* the content — reading files, downloading images, parsing a
 * template's `const` — stays in the caller.
 *
 * ## Findings
 *
 * `{level, code, field, message}`. `field` is the field slug the finding
 * belongs to, or null for one about the Item as a whole; it is what lets the
 * panel put the message under the right input. `tools/check-content.mjs`
 * ignores it and prints the message, which is why the messages are written to
 * read on their own.
 *
 * A **warning** never blocks. An **error** does.
 */

/** Webflow's own slug shape: lower-case words joined by single hyphens. */
export const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const HEX24_RE = /^[0-9a-f]{24}$/;
/** What the Webflow API writes, and what `new Date().toISOString()` writes. */
export const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

export const REFERENCE_TYPES = new Set(["Reference", "MultiReference"]);
export const STRING_TYPES = new Set(["PlainText", "RichText", "Link", "Email", "Color", "VideoLink"]);
export const IMAGE_TYPES = new Set(["Image", "MultiImage"]);
export const LIST_TYPES = new Set(["MultiReference", "MultiImage"]);

/**
 * The field a collection's pages take their `<meta name="description">` from.
 *
 * tools/check-parity.js fails the build on a page whose description is empty
 * (its "head floor"), and for these collections the template's data file uses
 * the field and nothing else — `data.item.description || ""` — so a Live Item
 * with it blank builds a page the parity check then rejects, at the very end of
 * a CI build. Read off each collection's `*.11tydata.js`; they agree with the
 * bindings in the `detail_*` records of content/pages.json.
 *
 * Provider, Testimonial and Link-and-App pages are left out on purpose: their
 * data files assemble a description when the field is blank. That is what keeps
 * /provider/michele-smith — the one Live Provider with no
 * `meta-description-seo` — building today (`describe()` in
 * src/provider/provider.11tydata.js).
 */
export const META_DESCRIPTION_FIELDS = {
  article: "description",
  assessment: "description-short",
  author: "bio-summary",
  books: "short-description",
  careers: "short-description",
  gallery: "description",
  news: "description",
  service: "description-long",
};

/**
 * The longest `<title>` Google shows before cutting it off, near enough.
 *
 * Only `seo-title` is measured against it, and only as a warning. Every other
 * title on the site is the exported one, and telling somebody their two-year-old
 * indexed title is four characters long is not a finding — but a title being
 * typed into the admin panel right now is worth a nudge.
 */
export const TITLE_LIMIT = 60;

/** Neither draft nor archived — CONTEXT.md's "Live". */
export function isLive(item) {
  return item.isDraft !== true && item.isArchived !== true;
}

export function status(item) {
  if (isLive(item)) return "Live";
  return [item.isDraft === true && "draft", item.isArchived === true && "archived"]
    .filter(Boolean)
    .join(", ");
}

export function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/* -------------------------------------------------------------- one field */

/**
 * @param {object} ctx
 * @param {string} ctx.collection      the collection's slug
 * @param {object} ctx.item            the Item the field belongs to
 * @param {object} ctx.field           its schema entry
 * @param {*}      ctx.value           `item.fieldData[field.slug]`
 * @param {Map<string, {collection: string, item: object}>} ctx.byId
 *        every Item in every collection, for resolving a Reference
 * @param {Map<string, string>} ctx.collectionSlugById
 *        collection id -> slug, from `collections/_index.json`
 * @param {Set<string>} ctx.loadedCollections
 *        which collections were actually read; a Reference into one that is
 *        missing is not reported, because its absence is already an error and
 *        every reference into it failing as well would bury that one line
 * @param {(hit: {collection: string, item: object}) => string} ctx.label
 * @returns {{level: string, code: string, field: string, message: string}[]}
 */
export function checkField({ collection, item, field, value, byId, collectionSlugById, loadedCollections, label }) {
  const findings = [];
  const at = `${field.slug} (${field.type})`;
  const add = (level, code, message) => findings.push({ level, code, field: field.slug, message });
  const error = (code, message) => add("error", code, message);
  const warn = (code, message) => add("warning", code, message);

  const empty = value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

  if (field.isRequired && isLive(item) && empty) {
    error("required", `${at} is required on a Live item and is empty`);
  }

  if (field.type === "Switch") {
    if (typeof value !== "boolean") error("type", `${at} must be true or false, not ${JSON.stringify(value)}`);
    return findings;
  }
  if (value === undefined || value === null) return findings;

  if (STRING_TYPES.has(field.type)) {
    if (typeof value !== "string") {
      error("type", `${at} must be a string or null`);
      return findings;
    }
    const max = field.validations && field.validations.maxLength;
    if (typeof max === "number" && value.length > max) {
      error("type", `${at} is ${value.length} characters; the schema allows ${max}`);
    }
    // A canonical names one page as the original of another, and a relative one
    // is ignored by every search engine that reads it — so a typo here does
    // nothing at all, quietly, which is why it is an error.
    if (field.slug === "canonical" && value.trim() && !/^https:\/\/[^\s]+$/.test(value.trim())) {
      error("canonical", `${at} must be a full https:// URL, not ${JSON.stringify(value)}`);
    }
    if (field.slug === "seo-title" && value.trim().length > TITLE_LIMIT) {
      warn(
        "seo-title",
        `seo-title is ${value.trim().length} characters; Google cuts a title off near ${TITLE_LIMIT}. ` +
          `It publishes as written — this is a nudge, not a refusal`
      );
    }
  } else if (field.type === "Number") {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      error("type", `${at} must be a number or null, not ${JSON.stringify(value)}`);
      return findings;
    }
    const v = field.validations || {};
    if (v.format === "integer" && !Number.isInteger(value)) error("type", `${at} must be a whole number`);
    if (v.allowNegative === false && value < 0) error("type", `${at} must not be negative`);
  } else if (field.type === "Option") {
    // The export stores the option's id, never its name. The loader would
    // happen to render a name too (lib/webflow-content.js falls back to the
    // raw value), but two spellings of one choice is how a filter somewhere
    // starts missing Items — so the id is the only valid form, and a name gets
    // told which id it means.
    const options = (field.validations && field.validations.options) || [];
    if (!options.some((o) => o.id === value)) {
      const named = options.find((o) => typeof value === "string" && o.name.toLowerCase() === value.trim().toLowerCase());
      error(
        "option",
        named
          ? `${at} is ${JSON.stringify(value)} — write the option's id, "${named.id}", not its name`
          : `${at} is ${JSON.stringify(value)}, which is not one of this field's option ids: ` +
              options.map((o) => `"${o.id}" (${o.name})`).join(", ")
      );
    }
  } else if (REFERENCE_TYPES.has(field.type)) {
    const target = collectionSlugById.get(field.validations && field.validations.collectionId);
    if (target && !loadedCollections.has(target)) return findings;
    const ids = field.type === "MultiReference" ? value : [value];
    if (!Array.isArray(ids)) {
      error("reference", `${at} must be a list of item ids or null`);
      return findings;
    }
    for (const id of ids) {
      const hit = typeof id === "string" ? byId.get(id) : undefined;
      if (!hit) {
        error("reference", `${at} points at ${JSON.stringify(id)}, which is not an item in any collection (expected one from ${target})`);
      } else if (hit.collection !== target) {
        error("reference", `${at} points at ${label(hit)}, but the field takes items from ${target}`);
      } else if (isLive(item) && !isLive(hit.item)) {
        warn("reference", `${at} points at ${label(hit)}, which is ${status(hit.item)} — the build drops the link, so it will not show`);
      }
    }
  } else if (field.type === "DateTime") {
    // The shape lib/published.js parses and the admin panel writes. A date
    // JavaScript cannot read would silently mean "no schedule" there, which is
    // the one wrong answer a typo should not be allowed to produce.
    if (typeof value !== "string" || !ISO_RE.test(value) || Number.isNaN(new Date(value).getTime())) {
      error("type", `${at} must be an ISO 8601 UTC timestamp like "2026-09-20T17:00:00.000Z", not ${JSON.stringify(value)}`);
    }
  } else if (IMAGE_TYPES.has(field.type)) {
    const assets = field.type === "MultiImage" ? value : [value];
    if (!Array.isArray(assets) || !assets.every((a) => isPlainObject(a) && typeof a.url === "string")) {
      error("image", `${at} must be ${field.type === "MultiImage" ? "a list of " : ""}{"url": "/images/<file>", "alt": "..."} or null`);
    }
  }

  return findings;
}

/* --------------------------------------------------------------- one Item */

/**
 * Everything that can be judged about one Item from its own contents and its
 * schema: metadata shape, slug, fields, and the meta description its page will
 * need.
 *
 * Not included, because none of it is about one Item: whether its images exist
 * on disk, whether a template pins it, whether the live site serves its URL,
 * whether the collection is in `createdOn` order. `tools/check-content.mjs`
 * does those.
 *
 * `slug-format` is reported as an error here. One published slug breaks the
 * rule and is frozen by ADR-0003; that is a pinned exception in
 * `tools/check-content.mjs`, which downgrades this finding for that Item. The
 * panel has no such exception, and should not: it never needs to create one.
 *
 * @param {object} ctx
 * @param {string} ctx.collection
 * @param {object} ctx.schema         `{fields: [...]}`
 * @param {object} ctx.item
 * @param {Map<string, {collection: string, item: object}>} ctx.byId
 * @param {Map<string, string>} ctx.collectionSlugById
 * @param {Set<string>} ctx.loadedCollections
 * @param {Date} [ctx.now]           the clock the date rules judge against; a test passes its own
 * @param {(slug: string) => object|null} [ctx.slugTakenBy]
 *        another Item in this collection already using this slug, or null.
 *        Drafts and archived Items count: Webflow enforced that too, and a
 *        clash surfaces the moment the draft is published.
 * @param {(hit: {collection: string, item: object}) => string} [ctx.label]
 * @returns {{level: string, code: string, field: string|null, message: string}[]}
 */
export function checkItem({
  collection,
  schema,
  item,
  byId = new Map(),
  collectionSlugById = new Map(),
  loadedCollections = new Set(),
  slugTakenBy = () => null,
  label = (hit) => `${hit.collection}/${hit.item?.fieldData?.slug ?? hit.item?.id}`,
  now = new Date(),
}) {
  const findings = [];
  const add = (level, code, field, message) => findings.push({ level, code, field, message });
  const error = (code, field, message) => add("error", code, field, message);

  if (!isPlainObject(item)) {
    error("item", null, "is not an object");
    return findings;
  }

  if (typeof item.id !== "string" || !HEX24_RE.test(item.id)) {
    error("id", null, `id ${JSON.stringify(item.id)} is not 24 lower-case hex characters`);
  }
  for (const key of ["isDraft", "isArchived"]) {
    if (typeof item[key] !== "boolean") {
      error("item", null, `${key} must be true or false, not ${JSON.stringify(item[key])}`);
    }
  }
  if (typeof item.cmsLocaleId !== "string" || !HEX24_RE.test(item.cmsLocaleId)) {
    error("item", null, `cmsLocaleId ${JSON.stringify(item.cmsLocaleId)} is not 24 hex characters`);
  }
  for (const key of ["createdOn", "lastUpdated", "lastPublished"]) {
    const value = item[key];
    // Webflow writes `lastPublished: null` for an Item that has never been
    // published — 27 of the 29 drafts and archived Items in the export carry
    // it. That is true of a draft, so it stays legal there. A Live Item needs
    // one: Articles and News print it as their date, and the home page picks
    // its three Articles by it. tools/new-item.mjs writes null on a draft for
    // the same reason, so that publishing one is caught here until the date is
    // set.
    if (key === "lastPublished" && value === null && !isLive(item)) continue;
    if (key === "lastPublished" && value === null) {
      error("date", null, `lastPublished is null on a Live item — set it to the time you publish, e.g. "2026-09-16T17:00:00.000Z" (UTC); Articles and News print it as their date`);
    } else if (typeof value !== "string" || !ISO_RE.test(value) || Number.isNaN(Date.parse(value))) {
      error("date", null, `${key} must be an ISO timestamp like "2026-09-16T17:00:00.000Z", not ${JSON.stringify(value)}`);
    }
  }

  if (!isPlainObject(item.fieldData)) {
    error("item", null, "has no fieldData object");
    return findings;
  }

  const data = item.fieldData;
  const fields = new Map((schema.fields || []).map((f) => [f.slug, f]));

  const slug = data.slug;
  if (typeof slug !== "string" || slug === "") {
    error("slug", "slug", "has no slug");
  } else {
    if (!SLUG_RE.test(slug)) {
      error("slug-format", "slug", `slug "${slug}" must be lower-case letters and digits joined by single hyphens (e.g. "new-autism-clinic")`);
    }
    const clash = slugTakenBy(slug);
    if (clash) {
      error("slug-duplicate", "slug", `slug "${slug}" is also used by item ${clash.id} (${status(clash)}) in ${collection}`);
    }
  }

  for (const key of Object.keys(data)) {
    if (!fields.has(key)) {
      error("field-unknown", key, `fieldData has "${key}", which the ${collection} schema does not declare — a typo is silently ignored by the build`);
    }
  }

  for (const field of fields.values()) {
    findings.push(
      ...checkField({
        collection,
        item,
        field,
        value: data[field.slug],
        byId,
        collectionSlugById,
        loadedCollections,
        label,
      })
    );
  }

  const describedBy = META_DESCRIPTION_FIELDS[collection];
  if (isLive(item) && fields.has(describedBy) && !String(data[describedBy] ?? "").trim()) {
    error(
      "description",
      describedBy,
      `${describedBy} is empty. It is this page's meta description, and the build's parity ` +
        `check fails on a page without one — fill it in (a sentence or two), or keep the item a draft`
    );
  }

  findings.push(...collectionItemRules({ collection, item, fields, data, now }));

  return findings;
}

/* ------------------------------------- rich text, as a browser reads it */

/**
 * The grammar the panel's sanitiser (admin/shared/markup.js) and the build's
 * content check share for rich text, so that what the panel refuses the build
 * refuses too — and a body that reaches `content/` by any other route (a direct
 * API call with an Editor's session, a hand edit in a pull request) fails
 * `npm run check:content` before it ships (audit S9, S10). One copy, here, for
 * the reason the header gives: two tokenisers that agree by coincidence stop
 * agreeing the day one is fixed.
 *
 * A tag is `<name attrs>`: a name starting with a letter, then attributes in
 * which `>` may appear only inside quotes. A `<` that a browser reads as the
 * start of markup — followed by a letter, `/`, `!` or `?` — but that this cannot
 * read as a whole tag or comment is a *malformed tag*: the browser finishes it
 * with the next `>` it finds, which may be the template's own, so
 * `<img src=x onerror=… //` with no `>` of its own runs on the page. A `<`
 * followed by anything else (`a < b`, `<3`) is text to a browser and to this.
 */
export const TAG_RE = /^<\s*(\/?)([a-zA-Z][a-zA-Z0-9-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/;

/** `href="/x" class="y"` -> `[["href", "/x"], ["class", "y"]]`, names lower-cased. */
export function parseAttrs(source) {
  const out = [];
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  let m;
  while ((m = re.exec(String(source ?? ""))) !== null) {
    out.push([m[1].toLowerCase(), m[3] ?? m[4] ?? m[5] ?? ""]);
  }
  return out;
}

/** What a link in a body may point at. Every existing body is http, https or mailto. */
export const LINK_SCHEMES = new Set(["http:", "https:", "mailto:", "tel:"]);
/** What an image in a body may load from. */
export const IMAGE_SCHEMES = new Set(["http:", "https:"]);

/**
 * Every attribute a browser reads as a URL it will fetch or run, and what each
 * may point at. `href` and `src` are the two the sanitiser stores; the rest are
 * here so that a body which never passed through it (`xlink:href` on an SVG
 * link, `formaction` on a button) is judged all the same.
 */
export const URL_ATTRS = {
  href: LINK_SCHEMES,
  "xlink:href": LINK_SCHEMES,
  action: LINK_SCHEMES,
  formaction: LINK_SCHEMES,
  src: IMAGE_SCHEMES,
  poster: IMAGE_SCHEMES,
  data: IMAGE_SCHEMES,
};

/**
 * Elements the sanitiser never stores and the build has no use for: the ones
 * it drops whole (a form, a stylesheet, a plugin) and the three whose own
 * grammar — SVG, MathML, a template — a text scan cannot follow. `script` and
 * `iframe` are not here: whether a `<script>` may be there at all (JSON-LD) is
 * the caller's rule, and both have had their own line in check-content since
 * before this twin existed.
 */
export const NEVER_STORED = new Set([
  "form", "input", "button", "textarea", "select", "meta", "base", "link", "style",
  "object", "embed", "svg", "math", "template",
]);

const NAMED_REFS = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: "\u00a0",
  colon: ":", semi: ";", sol: "/", bsol: "\\", num: "#", equals: "=", quest: "?", excl: "!",
  Tab: "\t", NewLine: "\n", lpar: "(", rpar: ")",
};
// The handful a browser decodes with no `;` after them (the legacy set), where
// `&amp` in a query string is the one that matters.
const LEGACY_REFS = new Set(["amp", "lt", "gt", "quot", "nbsp"]);

/**
 * An attribute value as a browser reads it: character references decoded.
 *
 * A browser decodes `href="javascript&colon;alert(1)"` — and `&#58;`, `&#x3A;`,
 * `java&Tab;script:`, `&#106;avascript:` — *before* it reads the scheme, so a
 * judgement on the raw string lets every one of them through. Numeric
 * references decode with or without the `;`, as in a browser; the named ones
 * decode only with it, except the legacy five above. Names not in the table
 * (the thousand symbols no scheme can be spelled with) are left as typed.
 *
 * @param {string} text
 * @returns {string}
 */
export function decodeEntities(text) {
  return String(text ?? "").replace(/&(?:#[xX]([0-9a-fA-F]+);?|#([0-9]+);?|([a-zA-Z]+)(;?))/g, (m, hex, dec, name, semi) => {
    if (hex !== undefined || dec !== undefined) {
      const code = hex !== undefined ? parseInt(hex, 16) : parseInt(dec, 10);
      if (!Number.isFinite(code) || code === 0 || code > 0x10ffff) return "\ufffd";
      return String.fromCodePoint(code);
    }
    if (!(name in NAMED_REFS)) return m;
    if (!semi && !LEGACY_REFS.has(name)) return m;
    return NAMED_REFS[name];
  });
}

/**
 * A URL as a browser will read it: `value` with the characters the URL parser
 * discards removed, and the `protocol` it will give it.
 *
 * A regular expression over the raw string is what the sanitiser used to have,
 * and `java\nscript:alert(1)` walked past it. Browsers strip ASCII tab and
 * newline anywhere in a URL, and C0 controls and spaces at either end, before
 * they read the scheme — so that string *is* `javascript:` to Chrome, and so is
 * one with a U+0001 in front. The URL parser does the same stripping, so it is
 * asked instead of a pattern. A relative link resolves against a placeholder
 * origin and therefore reads as `https:`, which is what a relative link is.
 *
 * @returns {{value: string, protocol: string}|null} null when empty or unparseable
 */
export function linkTarget(raw) {
  const value = String(raw ?? "")
    .trim()
    .replace(/[\t\n\r]/g, "")
    .replace(/^[\u0000- ]+|[\u0000- ]+$/g, "");
  if (!value) return null;
  try {
    return { value, protocol: new URL(value, "https://relative.invalid/").protocol };
  } catch {
    return null;
  }
}

const JSON_LD_BLOCK_RE = /<script\b[^>]*\btype\s*=\s*["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script\s*>/gi;
const JSON_LD_OPEN_RE = /<script\b[^>]*\btype\s*=\s*["']application\/ld\+json["'][^>]*>/gi;

/**
 * What in a rich-text body a browser would run or mis-read: the build's twin of
 * the panel's sanitiser. Each string names one defect; an empty array is clean.
 *
 *   - a malformed tag, or an unterminated comment (see TAG_RE above);
 *   - an `on*` attribute on any tag — an event handler is a `<script>` with a
 *     different spelling;
 *   - a URL attribute (URL_ATTRS: `href`, `src`, `xlink:href`, `action`,
 *     `formaction`, `poster`, `data`) whose scheme, read as a browser reads it
 *     — character references decoded first, then `linkTarget` — is not on that
 *     attribute's list;
 *   - an element the sanitiser never stores (NEVER_STORED: a form, a style, an
 *     svg, …), whatever its attributes say;
 *   - a JSON-LD block with no `</script>`: the browser reads the rest of the
 *     page into it.
 *
 * Closed JSON-LD blocks are left out of the scan: lib/rich-text.js passes them
 * to the page as written and their contents are data to a browser, not markup.
 * Whether a `<script>` may be there at all is the caller's rule, not this one's.
 *
 * @param {string} html
 * @returns {string[]}
 */
export function richTextDefects(html) {
  const defects = [];
  const source = String(html ?? "");
  const opened = (source.match(JSON_LD_OPEN_RE) || []).length;
  const closed = (source.match(JSON_LD_BLOCK_RE) || []).length;
  if (opened > closed) {
    defects.push(`has a JSON-LD <script> block that never closes — a browser swallows the rest of the page into it`);
  }
  const text = source.replace(JSON_LD_BLOCK_RE, "");
  const excerpt = (at) => JSON.stringify(text.slice(at, at + 40).replace(/\s+/g, " "));
  let i = 0;
  while ((i = text.indexOf("<", i)) !== -1) {
    if (text.startsWith("<!--", i)) {
      const end = text.indexOf("-->", i + 4);
      if (end === -1) {
        defects.push(`has a comment that never closes, at ${excerpt(i)} — a browser swallows the rest of the page into it`);
        break;
      }
      i = end + 3;
      continue;
    }
    if (!/[A-Za-z/!?]/.test(text[i + 1] || "")) {
      i += 1; // `a < b`, `<3`: text to a browser
      continue;
    }
    const match = TAG_RE.exec(text.slice(i));
    if (!match) {
      defects.push(`has a malformed tag at ${excerpt(i)} — a browser would finish it with the next ">" on the page, which may be the template's`);
      i += 1;
      continue;
    }
    const [, close, name, attrs] = match;
    if (!close) {
      const tag = name.toLowerCase();
      if (NEVER_STORED.has(tag)) {
        defects.push(`has a <${tag}>, which a body may not contain — the panel's sanitiser never stores one`);
      }
      for (const [key, raw] of parseAttrs(attrs)) {
        if (key.startsWith("on")) {
          defects.push(`has an event handler (${key}) on <${tag}>`);
        }
        const allowed = URL_ATTRS[key];
        if (allowed && raw.trim()) {
          const target = linkTarget(decodeEntities(raw));
          if (!target || !allowed.has(target.protocol)) {
            defects.push(`has a ${key} on <${tag}> whose scheme a browser would run or refuse: ${JSON.stringify(raw.slice(0, 40))}`);
          }
        }
      }
    }
    i += match[0].length;
  }
  return defects;
}

/* ------------------------------------------ one Item, per collection */

/** The text a rich-text field reads as: tags gone, entities and whitespace collapsed. */
export function plainText(html) {
  return String(html ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;|[ ​‍]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** The name an Option field's value stands for, or null. */
export function optionName(field, value) {
  const options = (field && field.validations && field.validations.options) || [];
  const hit = options.find((o) => o.id === value);
  return hit ? hit.name : null;
}

export const RATING_MIN = 1;
export const RATING_MAX = 5;

/** A quote shorter than this is a rating, not a review; it prints, but it is worth a look. */
export const SHORT_QUOTE_CHARS = 40;

/**
 * Words that promise an outcome. The CHCPBC standards (Marketing, Advertising
 * and Promotion 1.6) and CPSBC's advertising standard both forbid a review that
 * creates an unreasonable expectation or a guarantee; the clinic is answerable
 * for text a reviewer wrote once it publishes it. A warning, because "fixed"
 * has innocent uses and a human reads the sentence.
 *
 * Exported without the `g` flag on purpose: a global RegExp keeps `lastIndex`
 * between `.test()` calls, so the panel's rule (ADR-0010) would pass every
 * second quote. `outcomeClaims()` builds the global copy it needs itself.
 */
export const OUTCOME_CLAIM_RE = /\b(cured|guaranteed|fixed)\b|100\s*%/i;

/** The outcome words a quote uses, lower-cased, each once, in order of appearance. */
export function outcomeClaims(quote) {
  const all = String(quote ?? "").match(new RegExp(OUTCOME_CLAIM_RE.source, "gi")) || [];
  return [...new Set(all.map((w) => w.toLowerCase()))];
}

/**
 * A word that asks the reader to book: "book", "books", "booked", "booking"
 * (not "Facebook", not "bookkeeper"). Clients request an assessment; they
 * never book or choose a provider (the owner, 2026-10-13; ADR-0026). The
 * provider pages' own copy is held to that by their built-site tests; a
 * Provider's bio and meta description are the clinic's text, so for those it
 * is a warning here and in the panel, never a refusal: a clinician's edit must
 * not stop a deploy.
 *
 * Exported without the `g` flag, as OUTCOME_CLAIM_RE is.
 */
export const BOOKING_RE = /\bbook(?:s|ed|ing)?\b/i;

/** The sentences of `text` (HTML or plain) that ask the reader to book, trimmed, in order. */
export function bookingSentences(text) {
  return plainText(text)
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s && BOOKING_RE.test(s));
}

/** The warning both checks print, naming the field and its sentences. */
export function bookingMessage(field, sentences) {
  return (
    `${field} asks the reader to book: ${sentences.map((s) => `“${s}”`).join(" ")} — ` +
    `clients request an assessment, not a provider, and the provider pages say so (ADR-0026). ` +
    `Reword it (“Request an assessment…”) or take the sentence out`
  );
}

/** The one `consent-scope` under which a reviewer's photo may be shown. */
export const PHOTO_CONSENT_SCOPE = "Full name and photo";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Rules about one Item that only one collection has. The panel's `RULES` table
 * carries the same rules for the form; these are the twins that hold for a
 * hand edit too (ADR-0010: the panel may not be more permissive than the
 * build, and nothing stops a git commit from bypassing the panel).
 *
 * Errors and the consent warning are judged on Live Items only. A draft is
 * where a review is half-entered — the photo before the scope, the wage before
 * the duties — and the panel refuses to publish it until the rule holds.
 */
function collectionItemRules({ collection, item, fields, data, now }) {
  const findings = [];
  const add = (level, code, field, message) => findings.push({ level, code, field, message });
  const error = (code, field, message) => add("error", code, field, message);
  const warn = (code, field, message) => add("warning", code, field, message);
  const live = isLive(item);

  if (collection === "testimonials") {
    const rating = data.rating;
    if (typeof rating === "number" && Number.isInteger(rating) && (rating < RATING_MIN || rating > RATING_MAX)) {
      error("rating", "rating", `rating is ${rating}; a review is rated ${RATING_MIN} to ${RATING_MAX} stars`);
    } else if (live && (rating === null || rating === undefined)) {
      error("rating", "rating", `rating is empty on a Live review — the stars on the page are drawn from it; give it ${RATING_MIN} to ${RATING_MAX}`);
    }

    if (live && !data["consent-on"]) {
      warn(
        "consent",
        "consent-on",
        `consent-on is empty on a Live review. It is the date the reviewer agreed in writing to appear here; ` +
          `the clinic's consent pass sets it, and until that date is set this is a warning, not a refusal`
      );
    }

    const link = data["reviews-link-2"];
    if (live && typeof link === "string" && link !== "" && !/^https:\/\/\S+$/i.test(link)) {
      error(
        "link",
        "reviews-link-2",
        `reviews-link-2 is ${JSON.stringify(link)} — the page prints it as the reviewer's name link, so it must start with https:// ` +
          `(the panel refuses anything else; a hand edit must too)`
      );
    }

    const photo = isPlainObject(data["users-image"]) && typeof data["users-image"].url === "string" && data["users-image"].url;
    const scope = optionName(fields.get("consent-scope"), data["consent-scope"]);
    if (live && photo && scope !== PHOTO_CONSENT_SCOPE) {
      error(
        "consent-scope",
        "users-image",
        `users-image is set, but consent-scope is ${scope ? `"${scope}"` : "empty"} — a reviewer's photo needs ` +
          `"${PHOTO_CONSENT_SCOPE}". Remove the photo, or record that the reviewer agreed to it`
      );
    }

    if (live) {
      const quote = plainText(data["testimonial-content"]);
      if (quote.length < SHORT_QUOTE_CHARS) {
        warn("quote", "testimonial-content", `the quote is ${quote.length} characters (${JSON.stringify(quote)}) — a rating rather than a review; it publishes as written`);
      }
      const claims = outcomeClaims(quote);
      if (claims.length) {
        warn(
          "quote",
          "testimonial-content",
          `the quote says ${claims.map((w) => `"${w}"`).join(", ")} — a review must not promise an outcome or a guarantee ` +
            `(CHCPBC standard 1.6, CPSBC advertising standard). Read the sentence; if it is a claim, do not publish it as written`
        );
      }
    }
  }

  if (collection === "provider" && live) {
    for (const field of ["about", "meta-description-seo"]) {
      const sentences = bookingSentences(data[field]);
      if (sentences.length) warn("booking", field, bookingMessage(field, sentences));
    }
  }

  if (collection === "careers") {
    if (live && !String(data.wage ?? "").trim()) {
      error(
        "wage",
        "wage",
        `wage is empty on a Live posting. BC's Pay Transparency Act (s. 2) requires a publicly advertised job to state ` +
          `the expected wage or wage range — fill it in (a figure or a range), or keep the posting a draft`
      );
    }
    const posted = data["published-on"];
    const closes = data["closing-date"];
    if (typeof posted === "string" && typeof closes === "string" && ISO_RE.test(posted) && ISO_RE.test(closes) && Date.parse(closes) < Date.parse(posted)) {
      warn("closing-date", "closing-date", `closing-date (${closes}) is before published-on (${posted}) — the posting would close before it appears`);
    }
    // The same test lib/careers-closed.js makes for the templates and the
    // sitemap: past the date, the build renders the posting closed and drops
    // it from the index. It stays a warning — the page must keep existing until
    // someone unpublishes it (ADR-0003), and this is the reminder to.
    if (live && typeof closes === "string" && ISO_RE.test(closes) && Date.parse(closes) < now.getTime()) {
      warn("closed", "closing-date", `closing-date (${closes}) has passed — the site renders this posting as closed and keeps it out of search. Unpublish it once the position is filled`);
    }
    const email = data["apply-email"];
    if (typeof email === "string" && email.trim() && !EMAIL_RE.test(email.trim())) {
      error("apply-email", "apply-email", `apply-email is ${JSON.stringify(email)}, which is not an email address — the "Apply by email" button opens it`);
    }
  }

  return findings;
}

/* ------------------------------------------------------ one collection */

/** A question, as two spellings of it compare: case, spacing and the final "?" ignored. */
export function normaliseQuestion(name) {
  return String(name ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[\s?.!]+$/g, "")
    .trim();
}

/**
 * What can only be judged with the whole collection in view: the same review
 * or the same question published twice, two FAQs fighting for one position on
 * an assessment page. All warnings — each is a duplicate the clinic has to
 * resolve, and the pages build either way.
 *
 * Judged over Live Items only: a draft that repeats a Live review is the
 * ordinary state of a review being re-entered, and it is refused at Publish
 * by the same rule run then.
 *
 * @param {object} ctx
 * @param {string} ctx.collection
 * @param {object} ctx.schema         `{fields: [...]}`
 * @param {object[]} ctx.items        the whole collection
 * @param {Map<string, {collection: string, item: object}>} [ctx.byId]
 *        every Item in every collection, to name a referenced page
 * @param {(hit: {collection: string, item: object}) => string} [ctx.label]
 * @returns {{level: string, code: string, field: string, item: object, message: string}[]}
 *          `item` is the Item the finding is about; the message names the other
 */
export function checkCollection({
  collection,
  schema,
  items,
  byId = new Map(),
  label = (hit) => `${hit.collection}/${hit.item?.fieldData?.slug ?? hit.item?.id}`,
}) {
  const findings = [];
  const warn = (code, field, item, message) => findings.push({ level: "warning", code, field, item, message });
  const live = (Array.isArray(items) ? items : []).filter((i) => isPlainObject(i) && isPlainObject(i.fieldData) && isLive(i));
  const fields = new Map((schema.fields || []).map((f) => [f.slug, f]));
  const name = (item) => label({ collection, item });

  if (collection === "testimonials") {
    // Same words from the same reviewer. Three Live reviews say "Five Star
    // Clinic" and nothing else — three families, one phrase — so a quote under
    // SHORT_QUOTE_CHARS is not compared: it is already warned about as short.
    const seen = new Map();
    for (const item of live) {
      const quote = plainText(item.fieldData["testimonial-content"]);
      if (quote.length < SHORT_QUOTE_CHARS) continue;
      const key = `${String(item.fieldData["client-name"] ?? "").trim().toLowerCase()}\n${quote.toLowerCase()}`;
      const first = seen.get(key);
      if (first) {
        warn("duplicate", "testimonial-content", item, `is the same review as ${name(first)} — same quote, same reviewer — so it shows twice on /testimonials. Archive one of them`);
      } else {
        seen.set(key, item);
      }
    }
  }

  if (collection === "faq") {
    const seen = new Map();
    for (const item of live) {
      const key = normaliseQuestion(item.fieldData.name);
      if (!key) continue;
      const first = seen.get(key);
      if (first) {
        warn("duplicate", "name", item, `asks the same question as ${name(first)} — attach the one FAQ to both pages and archive the other, so the answer is written once`);
      } else {
        seen.set(key, item);
      }
    }

    // Assessment pages order their FAQs by `sort`; two Items sharing a value on
    // one page fall back to createdOn between themselves, which nobody chose.
    const category = fields.get("category");
    const taken = new Map();
    for (const item of live) {
      const data = item.fieldData;
      if (optionName(category, data.category) !== "Assessment" || typeof data.sort !== "number") continue;
      for (const id of Array.isArray(data.assessment) ? data.assessment : []) {
        const key = `${id}\n${data.sort}`;
        const first = taken.get(key);
        if (first) {
          const page = byId.get(id);
          warn(
            "sort",
            "sort",
            item,
            `sort ${data.sort} is also used by ${name(first)} on the same assessment page (${page ? label(page) : id}), ` +
              `so which comes first is left to createdOn — use the panel's Reorder card for that assessment to renumber them`
          );
        } else {
          taken.set(key, item);
        }
      }
    }
  }

  return findings;
}
